import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { ROUTES } from '@/routes/paths';
import { useUser } from '@/shared/hooks/useAuth';
import { useSpecialMasterItems, useSpecialMasterMutations, type SpecialMasterItem } from '../hooks/useMasters';

type Field = { key: string; label: string; type: 'text' | 'number' | 'select' | 'checkbox'; options?: string[]; default?: unknown; step?: string | number; min?: string | number };

interface SpecialConfig {
  label: string;
  description: string;
  fields: Field[];
  columns: { key: string; label: string; render?: (item: SpecialMasterItem) => string }[];
}

export const SPECIAL_CONFIG: Record<string, SpecialConfig> = {
  'additional-charges': {
    label: 'Additional Charges',
    description: 'Extra line-item charges available on quotations and invoices (freight, packing, installation…)',
    fields: [
      { key: 'name', label: 'Charge Name', type: 'text' },
      { key: 'type', label: 'Type', type: 'select', options: ['fixed', 'percent'], default: 'fixed' },
      { key: 'value', label: 'Value', type: 'number', default: 0, step: 'any' },
      { key: 'taxable', label: 'Taxable', type: 'checkbox', default: false },
    ],
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'type', label: 'Type' },
      { key: 'value', label: 'Value', render: (i) => `${i.value}${i.type === 'percent' ? '%' : ''}` },
      { key: 'taxable', label: 'Taxable', render: (i) => (i.taxable ? 'Yes' : 'No') },
    ],
  },
  'additional-concessions': {
    label: 'Additional Concessions',
    description: 'Discount line items available on quotations and invoices',
    fields: [
      { key: 'name', label: 'Concession Name', type: 'text' },
      { key: 'type', label: 'Type', type: 'select', options: ['percent', 'fixed'], default: 'percent' },
      { key: 'value', label: 'Value', type: 'number', default: 0, step: 'any' },
      { key: 'taxable', label: 'Taxable', type: 'checkbox', default: false },
    ],
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'type', label: 'Type' },
      { key: 'value', label: 'Value', render: (i) => `${i.value}${i.type === 'percent' ? '%' : ''}` },
    ],
  },
  // ONE FIELD, NOT TWO. This had a Title and a separate Terms text, because
  // the model was built for named blocks of rich text ("Standard Export
  // Terms", then a paragraph). RBM's terms are one-liners — the term IS its
  // own name — so asking for both meant typing the same sentence twice, and a
  // term that read one way in the quotation picker and another on the printed
  // document. The API mirrors this single value into `title` and `body`
  // (src/api-masters/index.js), so the picker's label and the text it inserts
  // can never disagree.
  'quotation-terms': {
    label: 'Quotation Terms',
    description: 'Reusable terms & conditions blocks for quotations',
    fields: [
      { key: 'title', label: 'Terms', type: 'text' },
      { key: 'isDefault', label: 'Default for new quotations', type: 'checkbox', default: false },
    ],
    columns: [
      { key: 'title', label: 'Terms' },
      { key: 'isDefault', label: 'Default', render: (i) => (i.isDefault ? 'Yes' : '—') },
    ],
  },
  'message-master': {
    label: 'Message Master',
    description: 'Reusable email/SMS/WhatsApp message templates',
    fields: [
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'channel', label: 'Channel', type: 'select', options: ['email', 'sms', 'whatsapp'], default: 'email' },
      { key: 'subject', label: 'Subject (email only)', type: 'text' },
      { key: 'body', label: 'Message body', type: 'text' },
    ],
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'channel', label: 'Channel' },
    ],
  },
  'tax-rates': {
    label: 'Tax Rates',
    description: 'GST rate presets used on cashbook and document line items',
    fields: [
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'rate', label: 'Rate (%)', type: 'number', default: 0, step: 'any' },
      { key: 'type', label: 'Type', type: 'select', options: ['CGST', 'IGST', 'SGST'], default: 'CGST' },
    ],
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'rate', label: 'Rate', render: (i) => `${i.rate}%` },
      { key: 'type', label: 'Type' },
    ],
  },
};

export function SpecialMasterPage({ inlineKind }: { inlineKind?: string } = {}) {
  const { kind: paramKind = '' } = useParams<{ kind: string }>();
  const kind = inlineKind || paramKind;
  const navigate = useNavigate();
  const config = SPECIAL_CONFIG[kind];

  const { data: items = [], isLoading } = useSpecialMasterItems(kind);
  const { create, update, bulkDelete } = useSpecialMasterMutations(kind);
  const user = useUser();
  // Permanent delete: quotation terms only, Admin / Super Admin only (client,
  // 2026-10-24). The server enforces the same rule.
  const canDelete = kind === 'quotation-terms' && ['superadmin', 'owner', 'admin'].includes(String(user?.role || ''));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmIds, setConfirmIds] = useState<string[] | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<SpecialMasterItem | null>(null);
  const [form, setForm] = useState<Record<string, unknown>>({});

  if (!config) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Unknown master "{kind}".</p>
        <Button variant="ghost" size="sm" className="mt-3" onClick={() => navigate(ROUTES.masterData)}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Master Data
        </Button>
      </div>
    );
  }

  const defaults = () => Object.fromEntries(config.fields.map((f) => [f.key, f.default ?? '']));
  const openNew = () => { setEditing(null); setForm(defaults()); setShowForm(true); };
  const openEdit = (item: SpecialMasterItem) => {
    setEditing(item);
    setForm(Object.fromEntries(config.fields.map((f) => [f.key, item[f.key] ?? f.default ?? ''])));
    setShowForm(true);
  };

  const handleSave = () => {
    if (!form.name && !form.title) { toast.error('A name is required.'); return; }
    const payload = { ...form };
    
    // Convert number fields before saving
    config.fields.forEach(f => {
      if (f.type === 'number' && payload[f.key] !== undefined && payload[f.key] !== '') {
        payload[f.key] = Number(payload[f.key]);
      }
    });

    if (editing) {
      update.mutate({ id: editing._id, ...payload }, {
        onSuccess: () => { toast.success('Updated.'); setShowForm(false); },
        onError: () => toast.error('Failed to update.'),
      });
    } else {
      create.mutate(payload, {
        onSuccess: () => { toast.success('Created.'); setShowForm(false); },
        onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to create.'),
      });
    }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(ROUTES.masterData)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{config.label}</h1>
          <p className="text-sm text-muted-foreground">{config.description}</p>
        </div>
      </div>

      <Card>
        <CardHeader
          title={`${config.label} (${items.length})`}
          action={<div className="flex gap-2">
            {canDelete && selected.size > 0 && (
              <Button size="sm" variant="danger" onClick={() => setConfirmIds([...selected])}>
                <Trash2 className="h-4 w-4 mr-1" /> Delete selected ({selected.size})
              </Button>
            )}
            <Button size="sm" onClick={openNew}><Plus className="h-4 w-4 mr-1" /> New</Button>
          </div>}
        />
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6"><Skeleton className="h-40" /></div>
          ) : items.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">Nothing here yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs uppercase bg-muted/50 border-b">
                  <tr>
                    {canDelete && (
                      <th className="w-10 px-4 py-3">
                        <input
                          type="checkbox"
                          aria-label="Select all"
                          className="h-4 w-4 accent-primary"
                          checked={items.length > 0 && selected.size === items.length}
                          onChange={(e) => setSelected(e.target.checked ? new Set(items.map((i) => i._id)) : new Set())}
                        />
                      </th>
                    )}
                    {config.columns.map((c) => <th key={c.key} className="px-4 py-3">{c.label}</th>)}
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map((item) => (
                    <tr key={item._id} className={selected.has(item._id) ? 'bg-danger/5' : 'hover:bg-muted/20'}>
                      {canDelete && (
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            aria-label="Select term"
                            className="h-4 w-4 accent-primary"
                            checked={selected.has(item._id)}
                            onChange={(e) => setSelected((prev) => { const next = new Set(prev); if (e.target.checked) next.add(item._id); else next.delete(item._id); return next; })}
                          />
                        </td>
                      )}
                      {config.columns.map((c) => (
                        <td key={c.key} className="px-4 py-3">{c.render ? c.render(item) : String(item[c.key] ?? '—')}</td>
                      ))}
                      <td className="px-4 py-3 text-center">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(item)} title="Edit">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {canDelete && (
                          <Button variant="ghost" size="sm" onClick={() => setConfirmIds([item._id])} title="Delete permanently" aria-label="Delete permanently">
                            <Trash2 className="h-3.5 w-3.5 text-danger-text" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={!!confirmIds}
        onClose={() => setConfirmIds(null)}
        title="Delete permanently?"
        size="sm"
        footer={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setConfirmIds(null)}>Cancel</Button>
            <Button
              variant="danger"
              disabled={bulkDelete.isPending}
              onClick={() => {
                if (!confirmIds) return;
                bulkDelete.mutate(confirmIds, {
                  onSuccess: (r) => {
                    toast.success(`${r.deleted} term${r.deleted === 1 ? '' : 's'} deleted permanently.`);
                    setSelected((prev) => { const next = new Set(prev); confirmIds.forEach((id) => next.delete(id)); return next; });
                    setConfirmIds(null);
                  },
                  onError: (err: any) => toast.error(err?.response?.data?.error || 'Could not delete.'),
                });
              }}
            >
              {bulkDelete.isPending ? 'Deleting…' : `Delete ${confirmIds?.length ?? 0} permanently`}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm">
          <p className="flex items-start gap-2 rounded-lg border border-danger/25 bg-danger/10 p-3 text-danger-text">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>This cannot be undone. The {confirmIds?.length === 1 ? 'term is' : `${confirmIds?.length ?? 0} terms are`} removed from the list and from the quotation picker. Quotations already made keep their text.</span>
          </p>
          <ul className="max-h-48 list-disc space-y-1 overflow-auto pl-5 text-muted-foreground">
            {items.filter((i) => confirmIds?.includes(i._id)).map((i) => (
              <li key={i._id}>{String(i.title || i.name || '').slice(0, 140)}</li>
            ))}
          </ul>
        </div>
      </Modal>

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? `Edit ${config.label}` : `New ${config.label}`}
        size="sm"
        footer={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={create.isPending || update.isPending}>
              {create.isPending || update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {config.fields.map((f) => {
            if (f.type === 'select') {
              return (
                <label key={f.key} className="block">
                  <span className="mb-1 block text-body-sm font-medium text-on-surface-variant">{f.label}</span>
                  <select
                    className="h-9 w-full rounded border border-outline bg-surface px-3 text-data-md"
                    value={String(form[f.key] ?? '')}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  >
                    {f.options!.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              );
            }
            if (f.type === 'checkbox') {
              return (
                <label key={f.key} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!form[f.key]}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.checked }))}
                  />
                  <span className="text-body-sm text-on-surface-variant">{f.label}</span>
                </label>
              );
            }
            return (
              <Input
                key={f.key}
                label={f.label}
                type={f.type === 'number' ? 'number' : 'text'}
                step={f.type === 'number' ? (f.step ?? 'any') : undefined}
                min={f.type === 'number' ? (f.min ?? 0) : undefined}
                value={String(form[f.key] ?? '')}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
              />
            );
          })}
        </div>
      </Modal>
    </div>
  );
}
